/**
 * FAQ Page Manager - Moon Moon Agency
 * Scroll fluide (Lenis) vers le groupe au clic sur un filtre + déploiement
 * des questions masquées au clic sur « Afficher toutes les questions ».
 */

/*********************************************************************************************
 * 🔧 FAQ PAGE MANAGER
 */
const FaqPageManager = {
	config: {
		selectors: {
			container: '.mm-faq-page__inner',
			filters: '.mm-faq-page__filters',
			filter: '.mm-faq-page__filter',
			moreBtn: '.mm-faq-page__more-btn',
			group: '.mm-faq-page__group',
			extra: '.mm-faq-page__item--extra',
		},
		classes: {
			active: 'is-active',
			dragging: 'is-dragging',
		},
	},

	init() {
		const container = window.getElement(this.config.selectors.container);
		if (!container) return; // Guard clause : pas de FAQ page sur cette page

		this.container = container;
		this._bindEvents();
		this._setupScrollSpy();
		this._setupDragScroll();
		window.log('info', '🔧 [FAQ_PAGE]', 'Initialized');
	},

	/* Surligne le filtre du groupe en haut du viewport (scrollspy) */
	_setupScrollSpy() {
		const groups = [...this.container.querySelectorAll(this.config.selectors.group)];
		if (!groups.length) return;

		this.spyGroups = groups;
		this._highlightActive(); // état initial

		this._onScroll = window.debounce(() => this._highlightActive(), 50);
		window.addEventListener('scroll', this._onScroll, { passive: true });
	},

	_highlightActive() {
		/* Groupe actif = dernier dont le haut a dépassé la ligne de référence (≈ sous le header) */
		const line = window.innerHeight * 0.25;
		let activeId = this.spyGroups[0].id;
		for (const group of this.spyGroups) {
			if (group.getBoundingClientRect().top <= line) {
				activeId = group.id;
			} else {
				break;
			}
		}

		this.container.querySelectorAll(this.config.selectors.filter).forEach((filter) => {
			filter.classList.toggle(this.config.classes.active, filter.dataset.faqFilter === activeId);
		});
	},

	/* Drag-to-scroll souris sur la barre de filtres (le scroll natif overflow-x
	   n'est pas draggable au pointeur sur desktop, et la scrollbar est masquée). */
	_setupDragScroll() {
		const bar = this.container.querySelector(this.config.selectors.filters);
		if (!bar) return;

		let startX = 0;
		let startScroll = 0;
		let pointerId = null;
		this._dragMoved = false;

		const stop = () => {
			if (pointerId === null) return;
			if (bar.hasPointerCapture(pointerId)) bar.releasePointerCapture(pointerId);
			pointerId = null;
			bar.classList.remove(this.config.classes.dragging);
		};

		bar.addEventListener('pointerdown', (e) => {
			/* Tactile : le scroll natif est déjà parfait, on ne s'en mêle pas. */
			if (e.pointerType === 'touch' || e.button !== 0) return;
			if (bar.scrollWidth <= bar.clientWidth) return; // rien à scroller

			pointerId = e.pointerId;
			startX = e.clientX;
			startScroll = bar.scrollLeft;
			this._dragMoved = false;
			/* `is-dragging` n'est posée qu'au franchissement du seuil (cf. pointermove) :
			   elle coupe les pointer-events des liens, donc l'ajouter dès le pointerdown
			   empêcherait tout clic simple d'aboutir. */
		});

		bar.addEventListener('pointermove', (e) => {
			if (pointerId !== e.pointerId) return;

			const delta = e.clientX - startX;
			/* Seuil : en dessous, c'est un clic, pas un drag. */
			if (!this._dragMoved) {
				if (Math.abs(delta) < 4) return;
				this._dragMoved = true;
				bar.setPointerCapture(pointerId);
				bar.classList.add(this.config.classes.dragging);
			}
			e.preventDefault(); // empêche la sélection de texte / le drag natif des liens
			bar.scrollLeft = startScroll - delta;
		});

		['pointerup', 'pointercancel', 'pointerleave'].forEach((type) => {
			bar.addEventListener(type, stop);
		});

		/* Un drag ne doit jamais démarrer le drag natif d'un <a>. */
		bar.addEventListener('dragstart', (e) => e.preventDefault());
	},

	_bindEvents() {
		this.container.addEventListener('click', (e) => {
			const moreBtn = e.target.closest(this.config.selectors.moreBtn);
			if (moreBtn) {
				this._expandGroup(moreBtn);
				return;
			}

			const filter = e.target.closest(this.config.selectors.filter);
			if (!filter) return;
			/* Un drag qui se termine sur un lien déclenche aussi un click : on l'annule. */
			if (this._dragMoved) {
				e.preventDefault();
				return;
			}
			this._scrollToTarget(e, filter);
		});
	},

	_expandGroup(btn) {
		const group = btn.closest(this.config.selectors.group);
		if (!group) return;

		group.querySelectorAll(this.config.selectors.extra).forEach((item) => {
			item.hidden = false;
		});
		group.dataset.collapsed = 'false';
		btn.setAttribute('aria-expanded', 'true');
		window.log('info', '🔧 [FAQ_PAGE]', 'Group expanded', { id: group.id });
	},

	_scrollToTarget(e, filter) {
		const hash = filter.getAttribute('href');
		if (!hash || !hash.startsWith('#')) return;

		const target = window.getElement(hash);
		if (!target) return;

		e.preventDefault(); // on gère toujours le scroll nous-mêmes (smooth garanti)
		const offset = this._headerOffset();

		/* Desktop avec Lenis actif : scroll fluide via Lenis. */
		if (window.LenisManager && window.LenisManager.instance && window.isDesktop()) {
			window.LenisManager.scrollTo(target, { offset: -offset });
		} else {
			/* Fallback (mobile, ou Lenis indisponible) : scroll natif smooth avec offset header. */
			const top = target.getBoundingClientRect().top + window.scrollY - offset;
			window.scrollTo({ top, behavior: 'smooth' });
		}

		history.replaceState(null, '', hash);
	},

	/* Hauteur du header sticky (navbar + barre d'annonce) depuis les variables CSS, avec marge. */
	_headerOffset() {
		const styles = getComputedStyle(document.documentElement);
		const navbar = parseInt(styles.getPropertyValue('--navbar-height'), 10) || 56;
		const announcement = parseInt(styles.getPropertyValue('--announcement-bar-height'), 10) || 32;
		return navbar + announcement + 20;
	},
};

// Auto-init avec vérification readyState
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => FaqPageManager.init());
} else {
	FaqPageManager.init();
}
